// Deep-capture search-string generator (v8 feature).
//
// Goal: surface the Pokémon in the user's box that are most worth deep-capturing
// next, by generating a Pokémon GO box-search string they can paste into the
// in-game search bar. PoGo's search syntax:
//   - comma  = OR
//   - &      = AND
//   - !x     = NOT x
//
// Pragmatic approach: instead of trying to express "every Pokémon NOT in my deep
// records" (impossibly long), express "the Pokémon I MOST want to deep-capture
// next" using filters that map to trade-priority signals (4*, shadow, lucky,
// shiny, XXL/XXS).

import type { InventoryRecord } from "./inventory";

export interface DeepCaptureSearchConfig {
  includeHundo: boolean;     // 4*
  includeLucky: boolean;     // lucky
  includeShadow: boolean;    // shadow
  includeShiny: boolean;     // shiny
  includeSized: boolean;     // xxl, xxs
  excludeTraded: boolean;    // !traded, applied with & to shiny only (matches user spec)
  excludeAlreadyDeep: boolean; // append !species for the first ~10 species already captured
}

export const DEFAULT_DEEP_CAPTURE_CONFIG: DeepCaptureSearchConfig = {
  includeHundo: true,
  includeLucky: true,
  includeShadow: true,
  includeShiny: true,
  includeSized: true,
  excludeTraded: true,
  excludeAlreadyDeep: true,
};

export interface DeepCapturePreset {
  id: string;
  label: string;
  config: DeepCaptureSearchConfig;
}

// Presets shown as chips above/below the generator. "Custom" is handled in the UI.
export const DEEP_CAPTURE_PRESETS: DeepCapturePreset[] = [
  {
    id: "smart",
    label: "Smart picks",
    config: DEFAULT_DEEP_CAPTURE_CONFIG,
  },
  {
    id: "hundos",
    label: "Hundos only",
    config: {
      includeHundo: true,
      includeLucky: false,
      includeShadow: false,
      includeShiny: false,
      includeSized: false,
      excludeTraded: false,
      excludeAlreadyDeep: false,
    },
  },
  {
    id: "lucky",
    label: "Lucky hunters",
    config: {
      includeHundo: false,
      includeLucky: true,
      includeShadow: false,
      includeShiny: false,
      includeSized: false,
      excludeTraded: true,
      excludeAlreadyDeep: false,
    },
  },
  {
    id: "shadow",
    label: "Shadow priority",
    config: {
      includeHundo: false,
      includeLucky: false,
      includeShadow: true,
      includeShiny: false,
      includeSized: false,
      excludeTraded: false,
      excludeAlreadyDeep: false,
    },
  },
  {
    id: "trade-bait",
    label: "Trade bait",
    config: {
      includeHundo: true,
      includeLucky: true,
      includeShadow: true,
      includeShiny: false,
      includeSized: false,
      excludeTraded: true,
      excludeAlreadyDeep: false,
    },
  },
];

const MAX_SPECIES_EXCLUSIONS = 10;

// Detect which preset (if any) matches the current config; returns "custom"
// when no preset matches.
export function detectActivePreset(config: DeepCaptureSearchConfig): string {
  for (const p of DEEP_CAPTURE_PRESETS) {
    let match = true;
    for (const key of Object.keys(p.config) as (keyof DeepCaptureSearchConfig)[]) {
      if (p.config[key] !== config[key]) { match = false; break; }
    }
    if (match) return p.id;
  }
  return "custom";
}

export interface DeepCaptureSearchResult {
  string: string;       // the generated PoGo box-search string
  termCount: number;    // how many terms it contains (sanity for "is this string useful?")
  excludedSpecies: string[]; // species names appended as !x
}


// Helper used by the Inventory hub progress bar.
export function deepRecordRatio(boxRecords: InventoryRecord[], totalBoxSize: number): {
  done: number;
  total: number;
  pct: number;
} {
  let done = 0;
  for (const r of boxRecords) {
    if (r.captureCompleteness === "complete") done++;
  }
  const total = totalBoxSize || boxRecords.length;
  const pct = total > 0 ? Math.min(100, (done / total) * 100) : 0;
  return { done, total, pct };
}
